import { type KeyboardEvent, type ReactNode, useEffect, useRef } from "react";

import { cn } from "../../foundations/cn.js";
import { useLayout } from "../../foundations/themeContext.js";

export interface ChatDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The region's name: "Trainer chat". */
  label: string;
  /** The ChatPanel. */
  children: ReactNode;
  className?: string;
}

/**
 * The store's chat beside the page: a panel inset from the right edge on desktop, a sheet from
 * the bottom on phones. The page stays visible and usable beside it. Opening it moves focus in
 * and Escape closes it; returning focus to the launcher is the caller's (it owns the launcher).
 */
export function ChatDrawer({ open, onOpenChange, label, children, className }: ChatDrawerProps) {
  const layout = useLayout();
  const ref = useRef<HTMLElement>(null);
  const wasOpen = useRef(open);
  // Focus follows the reader in when they open it, not when a page loads with it open.
  useEffect(() => {
    if (open && !wasOpen.current) ref.current?.focus();
    wasOpen.current = open;
  }, [open]);
  if (!open) return null;
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Escape" && !event.defaultPrevented) onOpenChange(false);
  };
  return (
    <aside
      ref={ref}
      aria-label={label}
      tabIndex={-1}
      onKeyDown={onKeyDown}
      className={cn(
        "fixed z-50 flex flex-col overflow-hidden border border-border-subtle shadow-float outline-none",
        // Glass chrome gets a denser glass than the nav's: chat is read for minutes, not glanced at.
        layout.chrome === "glass" ? "bg-bg-canvas/85 backdrop-blur-xl" : "bg-bg-surface",
        "max-md:inset-x-0 max-md:top-36 max-md:bottom-0 max-md:rounded-t-2xl max-md:border-b-0",
        "md:right-4 md:bottom-4 md:w-[380px] md:rounded-2xl",
        layout.header === "floating" ? "md:top-24" : "md:top-20",
        "transition-[translate,opacity] duration-base ease-enter motion-reduce:transition-none starting:opacity-0 max-md:starting:translate-y-6 md:starting:translate-x-6",
        className,
      )}
    >
      {children}
    </aside>
  );
}
